import React, { useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { DownloadCloud, Package, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ProgressBar } from '../common/ProgressBar';
import { useSlotCoreInstall } from '../../hooks/useSlotCoreInstall';
import type { SlotCoreStatus } from '../../hooks/useSlotCoreInstall';

/**
 * Toast notification shown at the bottom-right of the editor
 * during slot-core download, extraction, and installation.
 */
export const SlotCoreInstallToast: React.FC = () => {
  const {
    status,
    progress,
    error,
    install,
    update,
    cancel,
    retry,
    dismiss,
  } = useSlotCoreInstall();

  // Listen for install-requested events from useProjectCreation
  useEffect(() => {
    const handleInstallRequest = () => {
      install();
    };

    window.addEventListener('slot-core:install-requested', handleInstallRequest);
    return () => {
      window.removeEventListener('slot-core:install-requested', handleInstallRequest);
    };
  }, [install]);

  // Listen for update-requested events from context menu / badge
  useEffect(() => {
    const handleUpdateRequest = () => {
      update(); // Check for updates first, only download if new version exists
    };

    window.addEventListener('slot-core:update-requested', handleUpdateRequest);
    return () => {
      window.removeEventListener('slot-core:update-requested', handleUpdateRequest);
    };
  }, [update]);

  // Auto-dismiss on complete
  useEffect(() => {
    if (status === 'complete') {
      const timer = setTimeout(dismiss, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, dismiss]);

  const isVisible = status === 'downloading' || status === 'extracting' || status === 'complete' || status === 'error';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-4 right-4 z-[100] w-80 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-4"
        >
          <ToastContent
            status={status}
            progress={progress}
            error={error}
            onCancel={cancel}
            onRetry={retry}
            onDismiss={dismiss}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface ToastContentProps {
  status: SlotCoreStatus;
  progress: import('../../services/slot-core').SlotCoreProgress | null;
  error: string | null;
  onCancel: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}

const ToastContent: React.FC<ToastContentProps> = ({
  status,
  progress,
  error,
  onCancel,
  onRetry,
  onDismiss,
}) => {
  const formatBytes = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }, []);

  if (status === 'downloading') {
    const percent = progress?.totalBytes
      ? Math.round(((progress.bytesReceived || 0) / progress.totalBytes) * 100)
      : 0;
    const received = formatBytes(progress?.bytesReceived || 0);
    const total = progress?.totalBytes ? formatBytes(progress.totalBytes) : '?';

    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <DownloadCloud size={18} className="text-blue-400 flex-shrink-0" />
          <span className="text-sm text-zinc-200 font-medium">
            Downloading Slot Core...
          </span>
        </div>
        <ProgressBar
          progress={percent}
          size="sm"
          showPercentage={false}
          className="mb-2"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-500">
            {percent}% &middot; {received}/{total}
          </span>
          <button
            onClick={onCancel}
            className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </>
    );
  }

  if (status === 'extracting') {
    const filesProcessed = progress?.filesProcessed || 0;
    const totalFiles = progress?.totalFiles || 0;
    const percent = totalFiles > 0 ? Math.round((filesProcessed / totalFiles) * 100) : 0;

    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <Package size={18} className="text-yellow-400 flex-shrink-0" />
          <span className="text-sm text-zinc-200 font-medium">
            Installing Slot Core...
          </span>
        </div>
        <ProgressBar
          progress={percent}
          size="sm"
          showPercentage={false}
          className="mb-2"
        />
        <span className="text-xs text-zinc-500">
          {filesProcessed}/{totalFiles} files
        </span>
      </>
    );
  }

  if (status === 'complete') {
    const isAlreadyUpToDate = progress?.message === 'Already up to date';
    return (
      <div className="flex items-center gap-2">
        <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
        <div>
          <span className="text-sm text-zinc-200 font-medium">
            {isAlreadyUpToDate ? 'Slot Core is already up to date' : 'Slot Core installed successfully'}
          </span>
          {!isAlreadyUpToDate && (
            <div className="text-xs text-zinc-500">
              {progress?.filesProcessed || 0} files
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0" />
          <span className="text-sm text-zinc-200 font-medium">
            Slot Core installation failed
          </span>
        </div>
        {error && (
          <p className="text-xs text-zinc-400 mb-3 ml-[26px]">{error}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onRetry}
            className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 transition-colors"
          >
            Retry
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-1 text-xs rounded text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </>
    );
  }

  return null;
};
